import React, { memo } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, layoutGap } from '../../../../theme/theme';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import type { QuickFillRowMode } from '../../../../shared/types';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { quickFillActionTooltips } from '../../../../shared/config';
import { getWorkflowChromePalette } from '../../../../shared/ui/workflow/WorkflowStepComponents';
import {
  ResponsiveToolbarButton,
  ToolbarButtonGroup,
} from '../../../../shared/ui/workflow';
import { QuickFillAutoFillControls } from './QuickFillAutoFillControls';

export interface ActionBarProps {
  // Data state
  totalRows: number;
  selectedRowCount: number;
  selectedCellCount?: number;
  activeColumnLabel?: string | null;
  fillDownTargetCount?: number;
  modelSelectionCount?: number;
  rowMode?: QuickFillRowMode;
  isColumnsPanelOpen?: boolean;
  modelDataStale?: boolean;

  // Handlers
  onAutoFillAll: () => void;
  onAutoFillSelected: () => void;
  onAddToKeyLibrary: () => void;
  keyLibraryRowsReady?: boolean;
  onCopy: () => void;
  onPaste: () => void;
  onClear: () => void;
  onClearColumn?: () => void;
  onFillDown: () => void;
  onFindReplace: () => void;
  onExport: () => void;
  onImport: () => void;
  onAddColumns: () => void;
  onRefreshModel?: () => void;
  onSelectInModel?: () => void;
  onToggleRowMode?: (checked: boolean) => void;
  autoFillControlsRef?: React.RefObject<HTMLDivElement | null>;
  manageColumnsRef?: React.RefObject<HTMLButtonElement | null>;
  findReplaceRef?: React.RefObject<HTMLButtonElement | null>;
  rowModeToggleRef?: React.RefObject<HTMLLabelElement | null>;
  modelSelectRef?: React.RefObject<HTMLButtonElement | null>;
  csvToolsRef?: React.RefObject<HTMLDivElement | null>;
  utilityToolsRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Compact action bar for table operations
 * Primary actions stay visible while secondary utilities remain dense.
 */
export const ActionBar = memo<ActionBarProps>(({
  totalRows,
  selectedRowCount,
  selectedCellCount = 0,
  activeColumnLabel = null,
  fillDownTargetCount,
  modelSelectionCount = 0,
  rowMode = 'familyType',
  isColumnsPanelOpen = false,
  modelDataStale = false,
  onAutoFillAll,
  onAutoFillSelected,
  onAddToKeyLibrary,
  keyLibraryRowsReady = true,
  onCopy,
  onPaste,
  onClear,
  onClearColumn,
  onFillDown,
  onFindReplace,
  onExport,
  onImport,
  onAddColumns,
  onRefreshModel,
  onSelectInModel,
  onToggleRowMode,
  autoFillControlsRef,
  manageColumnsRef,
  findReplaceRef,
  rowModeToggleRef,
  modelSelectRef,
  csvToolsRef,
  utilityToolsRef,
}) => {
  const theme = useTheme();
  const isCompactActions = useMediaQuery(theme.breakpoints.down('sm'));
  const isShortHeight = useMediaQuery('(max-height:480px)');
  const isSmOrWider = useMediaQuery(theme.breakpoints.up('sm'));
  const isShortWideViewport = isShortHeight && isSmOrWider;
  const isMidDensityViewport = useMediaQuery(theme.breakpoints.down(1100));
  const isConstrainedViewport = isCompactActions || isShortWideViewport || isMidDensityViewport;
  const workflowPalette = getWorkflowChromePalette(theme, 'workspace');

  const hasData = totalRows > 0;
  const hasSelection = selectedRowCount > 0;
  const hasCellSelection = selectedCellCount > 0;
  const hasClearTarget = hasSelection || hasCellSelection;
  const hasActiveColumn = Boolean(activeColumnLabel && activeColumnLabel.trim());
  const hasModelSelectionTarget = modelSelectionCount > 0;
  const resolvedFillDownTargetCount = Math.max(0, fillDownTargetCount ?? selectedRowCount);
  const hasMultiSelection = resolvedFillDownTargetCount >= 2;
  const actionButtonHeight = isConstrainedViewport ? 28 : 30;
  const actionButtonRadius = `${borderRadius.md}px`;
  const showActionText = !isConstrainedViewport;
  const buttonSize = isConstrainedViewport ? 'small' : 'medium';
  const groupDensity = isConstrainedViewport ? 'compact' : 'standard';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isCompactActions ? layoutGap.tight : isShortWideViewport ? layoutGap.micro : 0.35,
        py: isCompactActions ? layoutGap.tight : isShortWideViewport ? 0.16 : layoutGap.micro,
        px: isShortWideViewport ? 0.45 : 0.55,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.76 : 0.28),
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${workflowPalette.surfaceBorder}`,
        minHeight: isShortWideViewport ? 30 : 34,
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        overflow: 'hidden',
        '& > *': {
          minWidth: 0,
        },
        [theme.breakpoints.down('md')]: {
          alignItems: 'center',
          flexDirection: 'row',
          overflow: 'hidden',
        },
      }}
      role="toolbar"
      aria-label="Quick Fill actions"
      data-testid="quickfill-action-bar"
      data-responsive-mode={isCompactActions ? 'compact' : 'standard'}
    >
      <ResponsiveToolbarButton
        icon={
          <RefreshIcon sx={{ fontSize: showActionText ? '0.9rem' : '1rem' }} />
        }
        label="Refresh"
        aria-label="Refresh model data from Revit"
        showText={showActionText}
        variant="outlined"
        color="secondary"
        onClick={onRefreshModel}
        help={modelDataStale
          ? quickFillActionTooltips.refreshModelStale
          : quickFillActionTooltips.refreshModel}
        size={buttonSize}
        sx={{
          flex: '0 0 auto',
        }}
      />

      <ResponsiveToolbarButton
        ref={manageColumnsRef}
        icon={
          <ViewColumnIcon sx={{ fontSize: showActionText ? '0.9rem' : '1rem' }} />
        }
        label="Columns"
        aria-label={
          isColumnsPanelOpen
            ? 'Close manage columns panel'
            : 'Open manage columns panel'
        }
        showText={showActionText}
        variant={isColumnsPanelOpen ? 'contained' : 'outlined'}
        color="secondary"
        active={isColumnsPanelOpen}
        onClick={onAddColumns}
        help={quickFillActionTooltips.addColumns}
        size={buttonSize}
        sx={{
          flex: '0 0 auto',
        }}
      />

      <QuickFillAutoFillControls
        hasData={hasData}
        hasSelection={hasSelection}
        keyLibraryRowsReady={keyLibraryRowsReady}
        showActionText={showActionText}
        buttonSize={buttonSize}
        groupDensity={groupDensity}
        controlsRef={autoFillControlsRef}
        onAutoFillAll={onAutoFillAll}
        onAutoFillSelected={onAutoFillSelected}
        onAddToKeyLibrary={onAddToKeyLibrary}
      />

      <ActionHelpTooltip help={quickFillActionTooltips.rowModeInstances} placement="bottom">
        <FormControlLabel
          ref={rowModeToggleRef}
          data-testid="quickfill-tour-target-row-mode"
          control={
            <Switch
              checked={rowMode === 'instance'}
              onChange={(event) => onToggleRowMode?.(event.target.checked)}
              size="small"
              slotProps={{
                input: { 'aria-label': 'Show every instance', role: 'switch' },
              }}
              sx={{
                width: theme.spacing(4.25),
                height: theme.spacing(2.5),
                p: 0,
                '& .MuiSwitch-switchBase': {
                  p: '2px',
                  '&.Mui-checked': {
                    transform: 'translateX(14px)',
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 16,
                  height: 16,
                },
                '& .MuiSwitch-track': {
                  borderRadius: `${borderRadius.full}px`,
                },
              }}
            />
          }
          label={showActionText ? '\u00A0Instances' : null}
          sx={{
            ml: 0,
            mr: 0.1,
            flex: '0 0 auto',
            height: actionButtonHeight,
            borderRadius: actionButtonRadius,
            px: 0,
            border: 'none',
            backgroundColor: 'transparent',
            '& .MuiFormControlLabel-label': {
              fontSize: '0.72rem',
              color: theme.palette.text.secondary,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            },
          }}
        />
      </ActionHelpTooltip>

      <Box sx={{ flex: '1 1 8px', minWidth: 4, display: { xs: 'none', lg: 'block' } }} />

      <ToolbarButtonGroup
        aria-label="Clipboard and file tools"
        density={groupDensity}
        sx={{
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          flex: '0 0 auto',
          width: 'auto',
          flexWrap: { xs: 'wrap', sm: isShortWideViewport ? 'nowrap' : 'wrap', lg: 'nowrap' },
        }}
      >
        <ToolbarButtonGroup
          ref={utilityToolsRef}
          aria-label="Clipboard and fill tools"
          data-testid="quickfill-tour-target-utility-tools"
          density={groupDensity}
        >
          <ResponsiveToolbarButton
            icon={<ContentCopyIcon sx={{ fontSize: '1rem' }} />}
            label="Copy"
            aria-label="Copy selected cells"
            showText={false}
            disabled={!hasSelection && !hasCellSelection}
            onClick={onCopy}
            help={quickFillActionTooltips.copy}
            disabledHelp={quickFillActionTooltips.copyDisabled}
            size={buttonSize}
          />
          <ResponsiveToolbarButton
            icon={<ContentPasteIcon sx={{ fontSize: '1rem' }} />}
            label="Paste"
            aria-label="Paste cells"
            showText={false}
            onClick={onPaste}
            help={quickFillActionTooltips.paste}
            size={buttonSize}
          />
          <ResponsiveToolbarButton
            icon={<ClearIcon sx={{ fontSize: '1rem' }} />}
            label="Clear"
            aria-label="Clear selected cells"
            showText={false}
            disabled={!hasClearTarget}
            onClick={onClear}
            help={quickFillActionTooltips.clear}
            disabledHelp={quickFillActionTooltips.clearDisabled}
            size={buttonSize}
            sx={{ '&:hover': { color: theme.palette.warning.main } }}
          />
          <ResponsiveToolbarButton
            icon={<DeleteSweepIcon sx={{ fontSize: '1rem' }} />}
            label="Clear column"
            aria-label="Clear active column"
            showText={false}
            disabled={!hasData || !hasActiveColumn || !onClearColumn}
            onClick={onClearColumn}
            help={quickFillActionTooltips.clearColumn}
            disabledHelp={quickFillActionTooltips.clearColumnDisabled}
            size={buttonSize}
            sx={{ '&:hover': { color: theme.palette.warning.main } }}
          />
          <ResponsiveToolbarButton
            icon={<ArrowDownwardIcon sx={{ fontSize: '1rem' }} />}
            label="Fill down"
            aria-label="Fill down"
            showText={false}
            disabled={!hasMultiSelection}
            onClick={onFillDown}
            help={quickFillActionTooltips.fillDown}
            disabledHelp={quickFillActionTooltips.fillDownDisabled}
            size={buttonSize}
            sx={{ color: theme.palette.info.main }}
          />
        </ToolbarButtonGroup>

        <ResponsiveToolbarButton
          ref={findReplaceRef}
          icon={<FindReplaceIcon sx={{ fontSize: '1rem' }} />}
          label="Find and replace"
          aria-label="Open find and replace"
          showText={false}
          onClick={onFindReplace}
          help={quickFillActionTooltips.findReplace}
          data-testid="quickfill-tour-target-find-replace"
          size={buttonSize}
        />

        <ResponsiveToolbarButton
          ref={modelSelectRef}
          icon={<CenterFocusStrongIcon sx={{ fontSize: '1rem' }} />}
          label="Select in Revit"
          aria-label="Select component in Revit"
          showText={false}
          disabled={!hasModelSelectionTarget || !onSelectInModel}
          onClick={onSelectInModel}
          help={quickFillActionTooltips.selectInModel}
          disabledHelp={quickFillActionTooltips.selectInModelDisabled}
          data-testid="quickfill-tour-target-model-selection"
          size={buttonSize}
          sx={{ color: theme.palette.primary.main }}
        />

        <ToolbarButtonGroup
          ref={csvToolsRef}
          aria-label="CSV tools"
          data-testid="quickfill-tour-target-csv-tools"
          density={groupDensity}
        >
          <ResponsiveToolbarButton
            icon={<FileDownloadIcon sx={{ fontSize: '1rem' }} />}
            label="Export CSV"
            aria-label="Export table to CSV"
            showText={false}
            disabled={!hasData}
            onClick={onExport}
            help={quickFillActionTooltips.exportCsv}
            disabledHelp={quickFillActionTooltips.exportCsvDisabled}
            size={buttonSize}
            sx={{ color: theme.palette.success.main }}
          />
          <ResponsiveToolbarButton
            icon={<FileUploadIcon sx={{ fontSize: '1rem' }} />}
            label="Import CSV"
            aria-label="Import table from CSV"
            showText={false}
            onClick={onImport}
            help={quickFillActionTooltips.importCsv}
            size={buttonSize}
            sx={{ color: theme.palette.success.main }}
          />
        </ToolbarButtonGroup>
      </ToolbarButtonGroup>
    </Box>
  );
});

ActionBar.displayName = 'ActionBar';

export default ActionBar;
